import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import SummaryCard from "../components/history/SummaryCard";
import RecordTimeline from "../components/history/RecordTimeline";
import { periodRangeLabel } from "../components/history/ScopePicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import ProseScaleControl from "./QueryPage/ProseScaleControl";
import { useProseScale } from "./QueryPage/hooks/useProseScale";
import { CaseReportSections } from "./caseReportSections";

const API = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : "/api";

async function shareFetch(path, options = {}) {
  return fetch(`${API}${path}`, {
    ...options,
    credentials: "include",
    cache: "no-store",
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });
}

function trimNumber(value) {
  const rounded = Math.round(Number(value) * 10) / 10;
  if (rounded === Math.trunc(rounded)) return String(Math.trunc(rounded));
  return rounded.toFixed(1);
}

function formatHeight(cm, system) {
  if (system === "imperial") {
    const total = Math.round(cm / 2.54);
    const feet = Math.floor(total / 12);
    const inches = total % 12;
    return `${feet} ft ${inches} in`;
  }
  return `${trimNumber(cm)} cm`;
}

function formatWeight(kg, system) {
  if (system === "imperial") return `${trimNumber(kg * 2.2046226218)} lb`;
  return `${trimNumber(kg)} kg`;
}

function profileLine(t, block) {
  if (!block) return t("history.notProvided");
  const items = Array.isArray(block.items)
    ? block.items.map((item) => String(item).trim()).filter(Boolean)
    : [];
  const other = typeof block.other === "string" ? block.other.trim() : "";
  const parts = other && !items.includes(other) ? [...items, other] : items;
  if (parts.length > 0) return parts.join(", ");
  if (block.none_reported) return t("history.noneReported");
  return t("history.notProvided");
}

function statusText(t, group, value) {
  const key = `healthProfile.${group}.${value}`;
  const shown = t(key);
  return shown === key ? String(value) : shown;
}

function vitalLines(t, vitals) {
  if (!vitals || typeof vitals !== "object") return [];
  const system = vitals.unit_system === "imperial" ? "imperial" : "metric";
  const lines = [];
  if (typeof vitals.height_cm === "number") {
    lines.push(`${t("history.height")}: ${formatHeight(vitals.height_cm, system)}`);
  }
  if (typeof vitals.weight_kg === "number") {
    lines.push(`${t("history.weight")}: ${formatWeight(vitals.weight_kg, system)}`);
  }
  if (typeof vitals.bmi === "number") {
    lines.push(`${t("history.bmi")}: ${vitals.bmi}`);
  }
  const habits = [
    ["smoking", "smoking", "smoking"],
    ["alcohol", "alcohol", "alcohol"],
    ["activity_level", "activity", "activity"],
  ];
  habits.forEach(([field, labelKey, group]) => {
    const raw = String(vitals[field] || "").trim();
    if (!raw) return;
    lines.push(`${t(`history.${labelKey}`)}: ${statusText(t, group, raw)}`);
  });
  if (typeof vitals.sleep_hours_typical === "number") {
    lines.push(`${t("history.sleep")}: ${vitals.sleep_hours_typical}`);
  }
  return lines;
}

function ProfileRow({ testId, label, value }) {
  if (!value) return null;
  return (
    <p className="text-sm" data-testid={testId}>
      <span className="font-medium">{label}</span>
      {": "}
      {value}
    </p>
  );
}

function ShareProfile({ summary, t }) {
  const vitals = vitalLines(t, summary.vitals_lifestyle);
  return (
    <div className="space-y-2" data-testid="share-profile">
      {summary.profile_updated_at ? (
        <p className="text-sm text-muted-foreground" data-testid="share-profile-as-of">
          {t("history.profileAsOf").replace(
            "{date}",
            String(summary.profile_updated_at).slice(0, 10),
          )}
        </p>
      ) : null}
      <ProfileRow
        testId="share-allergies"
        label={t("history.allergies")}
        value={profileLine(t, summary.allergies)}
      />
      <ProfileRow
        testId="share-medications"
        label={t("history.medications")}
        value={profileLine(t, summary.medications)}
      />
      {summary.pregnancy_status ? (
        <ProfileRow
          testId="share-pregnancy"
          label={t("history.pregnancy")}
          value={statusText(t, "pregnancy", summary.pregnancy_status)}
        />
      ) : null}
      <ProfileRow
        testId="share-conditions"
        label={t("history.activeProblems")}
        value={profileLine(t, summary.conditions)}
      />
      {vitals.length > 0 ? (
        <div data-testid="share-vitals">
          <p className="text-sm font-medium">{t("history.vitals")}</p>
          {vitals.map((line) => (
            <p key={line} className="text-sm">{line}</p>
          ))}
        </div>
      ) : null}
      {summary.surgeries_text ? (
        <ProfileRow
          testId="share-surgeries"
          label={t("history.surgeries")}
          value={summary.surgeries_text}
        />
      ) : null}
    </div>
  );
}

function filenameFromDisposition(header) {
  if (!header) return "medical-history";
  const starred = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (starred) {
    try {
      return decodeURIComponent(starred[1]);
    } catch {
      return "medical-history";
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : "medical-history";
}

export default function SharedRecordPage() {
  const { token } = useParams();
  const { t, language } = useLanguage();
  const textScale = useProseScale();
  const [phase, setPhase] = useState("loading");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [preview, setPreview] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    let referrer = document.querySelector('meta[name="referrer"]');
    if (!referrer) {
      referrer = document.createElement("meta");
      referrer.setAttribute("name", "referrer");
      document.head.appendChild(referrer);
    }
    referrer.setAttribute("content", "no-referrer");
  }, []);

  // Returns true when a no-code share was unlocked and the caller may retry once.
  const handleUnlockRequired = async (response, { retried, expired }) => {
    let codeRequired = true;
    try {
      const body = await response.json();
      codeRequired = body?.error?.code_required !== false;
    } catch {
      codeRequired = true;
    }
    if (!codeRequired && !retried) {
      const unlocked = await shareFetch(`/share/${token}/unlock`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (unlocked.ok) return true;
      if (unlocked.status === 404) {
        setPhase("missing");
        return false;
      }
    }
    setPreview(null);
    setCode("");
    setCodeError(expired ? t("history.shareSessionExpired") : "");
    setPhase("code");
    return false;
  };

  const loadViewer = async (retried = false) => {
    const recordsRes = await shareFetch(`/share/${token}/records`);
    if (recordsRes.status === 404) {
      setPhase("missing");
      return;
    }
    if (recordsRes.status === 401) {
      if (await handleUnlockRequired(recordsRes, { retried, expired: false })) {
        await loadViewer(true);
      }
      return;
    }
    if (!recordsRes.ok) {
      setPhase("missing");
      return;
    }
    const recordsBody = await recordsRes.json();
    setRecords(recordsBody?.data?.items || []);
    const summaryRes = await shareFetch(`/share/${token}/summary`);
    if (summaryRes.ok) {
      const summaryBody = await summaryRes.json();
      setSummary(summaryBody?.data || null);
    } else {
      setSummary(null);
    }
    setPhase("ready");
  };

  useEffect(() => {
    loadViewer().catch(() => setPhase("missing"));
    // The token is the page identity. Reloading on token change is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const unlock = async () => {
    setCodeError("");
    const response = await shareFetch(`/share/${token}/unlock`, {
      method: "POST",
      body: JSON.stringify({ code: code.trim() }),
    });
    if (response.status === 404) {
      setPhase("missing");
      return;
    }
    if (response.status === 429) {
      setCodeError(t("history.shareLocked"));
      return;
    }
    if (!response.ok) {
      setCodeError(t("history.shareInvalidCode"));
      return;
    }
    setPhase("loading");
    await loadViewer();
  };

  const download = async (format, fallbackName, retried = false) => {
    const response = await shareFetch(`/share/${token}/export/${format}`);
    if (response.status === 401) {
      if (await handleUnlockRequired(response, { retried, expired: true })) {
        await download(format, fallbackName, true);
      }
      return;
    }
    if (response.status === 404) {
      setPhase("missing");
      return;
    }
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filenameFromDisposition(response.headers.get("content-disposition")) || fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const loadShareCase = async (item, retried) => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    const response = await shareFetch(`/share/${token}/records/${item.record_id}`);
    if (response.status === 401) {
      if (await handleUnlockRequired(response, { retried, expired: true })) {
        await loadShareCase(item, true);
      }
      return;
    }
    if (response.status === 404) {
      setPreview({ kind: "missing", item });
      return;
    }
    if (!response.ok) {
      setPreview({ kind: "notes", item });
      return;
    }
    const body = await response.json();
    const detail = body?.data || item;
    setPreview({
      kind: "case",
      item: detail,
      consultation: detail.consultation || null,
    });
  };

  const loadShareFile = async (item, fileIndex, retried) => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    if (item.kind === "case") {
      await loadShareCase(item, retried);
      return;
    }
    if (!item.file) {
      setPreview({ kind: "notes", item });
      return;
    }
    const query = fileIndex ? `?file_index=${encodeURIComponent(fileIndex)}` : "";
    const response = await shareFetch(`/share/${token}/records/${item.record_id}/file${query}`);
    if (response.status === 401) {
      if (await handleUnlockRequired(response, { retried, expired: true })) {
        await loadShareFile(item, fileIndex, true);
      }
      return;
    }
    if (response.status === 404) {
      setPreview({ kind: "missing", item });
      return;
    }
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const type = item.file.content_type || blob.type || "";
    if (type.startsWith("image/")) setPreview({ kind: "image", url, item });
    else if (type === "application/pdf") setPreview({ kind: "pdf", url, item });
    else setPreview({ kind: "file", url, item });
  };

  const openRecord = async (item, fileIndex = 0) => {
    setOpeningId(item.record_id);
    try {
      await loadShareFile(item, fileIndex, false);
    } finally {
      setOpeningId((current) => (current === item.record_id ? null : current));
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="shared-record-page">
      <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4">
        {phase !== "missing" ? (
          <div className="flex justify-end">
            <ProseScaleControl
              scale={textScale.scale}
              onDecrease={textScale.decrease}
              onIncrease={textScale.increase}
              canDecrease={textScale.canDecrease}
              canIncrease={textScale.canIncrease}
            />
          </div>
        ) : null}
        <div
          className="space-y-4"
          style={{ zoom: textScale.contentZoom }}
          data-testid="share-text-scale"
          data-text-scale={textScale.scale}
        >
        {phase === "loading" && (
          <p className="text-sm text-muted-foreground">{t("history.shareLoading")}</p>
        )}
        {phase === "missing" && (
          <p className="text-sm" data-testid="share-not-found">{t("history.shareNotFound")}</p>
        )}
        {phase === "code" && (
          <form
            className="space-y-3 max-w-sm"
            data-testid="share-code-form"
            onSubmit={(event) => {
              event.preventDefault();
              unlock();
            }}
          >
            <p className="text-sm font-medium">{t("history.shareCodePrompt")}</p>
            <p className="text-xs text-muted-foreground">{t("history.shareCodeOptional")}</p>
            <label className="block text-sm">
              {t("history.shareCodeLabel")}
              <Input
                value={code}
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                onChange={(event) => setCode(event.target.value)}
                data-testid="share-code-input"
                className="mt-1"
              />
            </label>
            {codeError ? <p className="text-sm text-destructive">{codeError}</p> : null}
            <Button type="submit" data-testid="share-code-continue">
              {t("history.shareCodeContinue")}
            </Button>
          </form>
        )}
        {phase === "ready" && (
          <>
            {summary?.note_for_doctor ? (
              <p
                className="text-sm rounded-md border border-border bg-muted/40 p-3 whitespace-pre-wrap"
                data-testid="share-doctor-note"
              >
                <span className="font-medium">{t("history.shareDoctorNote")}</span>
                {": "}
                {summary.note_for_doctor}
              </p>
            ) : null}
            {summary?.identity?.legal_name || summary?.identity?.date_of_birth ? (
              <header data-testid="share-identity">
                {summary.identity.legal_name ? (
                  <p className="text-base font-medium">{summary.identity.legal_name}</p>
                ) : null}
                {summary.identity.date_of_birth ? (
                  <p className="text-sm text-muted-foreground">
                    {t("history.exportDateOfBirth")}
                    {": "}
                    {summary.identity.date_of_birth}
                  </p>
                ) : null}
              </header>
            ) : summary?.age_display ? (
              <p className="text-sm" data-testid="share-age">
                {summary.age_display}
              </p>
            ) : null}
            <p className="text-sm" data-testid="share-period">
              {periodRangeLabel(summary?.period, t, language)}
            </p>
            {summary?.included ? <ShareProfile summary={summary} t={t} /> : null}
            <p
              className="text-sm rounded-md border border-border bg-muted/40 p-3"
              data-testid="share-banner"
            >
              {t("history.shareBanner")}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => download("pdf", "medical-history.pdf")} data-testid="share-download-pdf">
                {t("history.shareDownloadPdf")}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => download("fhir_ips", "international-patient-summary.json")} data-testid="share-download-fhir">
                {t("history.shareDownloadFhir")}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => download("zip", "medical-history.zip")} data-testid="share-download-zip">
                {t("history.shareDownloadZip")}
              </Button>
            </div>
            {summary?.included ? <SummaryCard summary={summary} readOnly omitClinical /> : null}
            <RecordTimeline
              records={records}
              onOpen={openRecord}
              expandCaseGroups
              openingId={openingId}
            />
            {preview ? (
              <Dialog
                open
                onOpenChange={(open) => {
                  if (!open) setPreview(null);
                }}
              >
                <DialogContent
                  className="max-w-4xl max-h-[90vh] overflow-y-auto"
                  style={{ zoom: textScale.contentZoom }}
                  data-testid="share-preview"
                >
                  <DialogHeader>
                    <div className="flex items-center justify-between gap-2 pr-6">
                      <DialogTitle className="text-sm font-medium text-left">
                        {preview.item?.title || t("history.shareViewFile")}
                      </DialogTitle>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setPreview(null)}>
                        {t("history.shareCloseFile")}
                      </Button>
                    </div>
                    <DialogDescription className="sr-only">
                      {t("history.shareViewFile")}
                    </DialogDescription>
                  </DialogHeader>
                  {Array.isArray(preview.item?.files) && preview.item.files.length > 1 && (
                    <div className="flex flex-wrap gap-2">
                      {preview.item.files.map((file) => (
                        <Button
                          key={file.index}
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => openRecord(preview.item, file.index)}
                        >
                          {file.filename || t("history.download")}
                        </Button>
                      ))}
                    </div>
                  )}
                  {preview.kind === "case" && (
                    <div className="space-y-3" data-testid="share-case-report">
                      {preview.consultation ? (
                        <CaseReportSections t={t} query={preview.consultation} />
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t("history.previewUnavailable")}
                        </p>
                      )}
                    </div>
                  )}
                  {preview.kind === "notes" && (
                    <p className="text-sm whitespace-pre-wrap">{preview.item?.notes || t("history.previewUnavailable")}</p>
                  )}
                  {preview.kind === "missing" && (
                    <p className="text-sm">{t("history.fileUnavailable")}</p>
                  )}
                  {preview.kind === "image" && (
                    <img
                      src={preview.url}
                      alt={preview.item?.title || ""}
                      className="max-h-[70vh] max-w-full object-contain mx-auto"
                    />
                  )}
                  {preview.kind === "pdf" && (
                    <iframe title={t("history.shareViewFile")} src={preview.url} className="w-full h-[70vh] border-0" />
                  )}
                  {preview.kind === "file" && (
                    <a href={preview.url} download className="text-sm text-primary underline">
                      {t("history.download")}
                    </a>
                  )}
                </DialogContent>
              </Dialog>
            ) : null}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
